<template>
  <button
    v-if="effectiveFeatureEnabled"
    ref="buttonRef"
    type="button"
    class="follow-control"
    :class="[
      `follow-control--${variant}`,
      {
        'follow-control--following': following,
        'follow-control--loading': loading,
      },
    ]"
    :role="variant === 'menu' ? 'menuitemcheckbox' : undefined"
    :aria-label="accessibleLabel"
    :aria-pressed="variant === 'identity' ? String(following) : undefined"
    :aria-checked="variant === 'menu' ? String(following) : undefined"
    :aria-busy="String(loading)"
    :disabled="isDisabled"
    v-haptic
    @click="handleToggle"
  >
    <UnitSpinner
      v-if="loading"
      class="follow-control__spinner"
      :size="14"
      :thickness="2"
      track-color="var(--color-control-track)"
      indicator-color="var(--color-text-primary)"
      :aria-hidden="true"
    />
    <span class="follow-control__label">{{ visibleLabel }}</span>
  </button>
</template>

<script setup>
import { computed, ref } from "vue";
import UnitSpinner from "@/components/GenericComponents/UnitSpinner.vue";
import { isFollowUiEnabled } from "@/config/runtimeConfig";

const props = defineProps({
  featureEnabled: {
    type: Boolean,
    default: isFollowUiEnabled,
  },
  following: {
    type: Boolean,
    default: false,
  },
  loading: {
    type: Boolean,
    default: false,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  variant: {
    type: String,
    default: "identity",
    validator: (value) => ["identity", "menu"].includes(value),
  },
  targetType: {
    type: String,
    default: "user",
    validator: (value) =>
      ["user", "person", "author", "organisation", "organization"].includes(
        value,
      ),
  },
  targetLabel: {
    type: String,
    default: "",
  },
});

const emit = defineEmits(["toggle"]);
const buttonRef = ref(null);
const effectiveFeatureEnabled = computed(
  () => isFollowUiEnabled && props.featureEnabled,
);

const visibleLabel = computed(() => (props.following ? "Unfollow" : "Follow"));

const accessibleTargetType = computed(() =>
  ["organisation", "organization"].includes(props.targetType)
    ? "organisation"
    : "person",
);

const accessibleLabel = computed(() => {
  const targetLabel = props.targetLabel.trim();
  return [visibleLabel.value, accessibleTargetType.value, targetLabel]
    .filter(Boolean)
    .join(" ");
});

const isDisabled = computed(() => props.disabled || props.loading);

const handleToggle = () => {
  if (isDisabled.value) return;
  emit("toggle", !props.following);
};

const focus = () => buttonRef.value?.focus();

defineExpose({ focus });
</script>

<style scoped>
.follow-control {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: var(--space-2);
  max-width: 100%;
  margin: 0;
  border: 0;
  border-radius: var(--radius-pill);
  background: transparent;
  color: var(--color-text-primary);
  font-family: var(--font-family-ui);
  font-weight: var(--font-weight-normal);
  line-height: 1;
  cursor: pointer;
  transition:
    color var(--motion-standard) ease-in,
    background-color var(--motion-standard) ease-in,
    border-color var(--motion-standard) ease-in,
    box-shadow var(--motion-standard) ease-in,
    transform var(--motion-standard) ease-in;
}

.follow-control--identity {
  justify-content: flex-start;
  min-height: var(--control-height-dense);
  padding-inline: 0;
  font-size: var(--font-size-label);
  white-space: nowrap;
}

.follow-control--menu {
  justify-content: flex-start;
  min-height: var(--control-height-compact);
  width: 100%;
  padding: var(--space-4);
  font-size: var(--font-size-label);
  box-shadow: none;
}

.follow-control--following {
  color: var(--color-accent-emphasis);
}

.follow-control:hover:not(:disabled) {
  color: var(--color-accent-emphasis);
  transform: translateY(var(--motion-pressed-offset));
}

.follow-control--menu:hover:not(:disabled) {
  background-color: var(--color-surface-menu-hover);
  transform: none;
}

.follow-control:focus-visible {
  outline: var(--focus-ring-width) solid var(--focus-ring-color);
  outline-offset: var(--focus-ring-offset);
}

.follow-control:disabled {
  cursor: not-allowed;
  opacity: 0.6;
  transform: none;
}

.follow-control__spinner {
  flex: 0 0 auto;
}

.follow-control__label {
  overflow: hidden;
  text-overflow: ellipsis;
}

.follow-control--identity .follow-control__label {
  transform: translateY(calc(var(--motion-pressed-offset) * -1));
}

@media only screen and (max-width: 590px) {
  .follow-control--identity,
  .follow-control--menu {
    min-width: var(--control-height-default);
    min-height: var(--control-height-default);
  }
}

@media (prefers-reduced-motion: reduce) {
  .follow-control {
    transition: none;
  }
}
</style>
